import { httpRequest } from './http.js'

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  return httpRequest<T>(path, opts)
}

const get   = <T>(p: string)              => apiFetch<T>(p)
const post  = <T>(p: string, b?: unknown) => apiFetch<T>(p, { method: 'POST',  body: b ? JSON.stringify(b) : undefined })
const patch = <T>(p: string, b: unknown)  => apiFetch<T>(p, { method: 'PATCH', body: JSON.stringify(b) })

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MediaFile {
  name:         string
  path:         string   // chemin absolu Windows
  casparPath?:  string   // chemin relatif CasparCG sans extension
  relativePath: string   // chemin relatif avec extension
  size:         number   // bytes
  ext?:         string
  type:         'file' | 'directory'
}

export interface ProbeResult {
  fileName:      string
  absolutePath?: string
  filePath:      string   // casparPath
  codec:         string | null
  container:     string | null
  durationSec:   number | null
  durationMs:    number | null
  resolution:    string | null
  fileSizeBytes: string | null
  status:        'OK' | 'ERROR' | string
  error?:        string
}

export type InterludeFileState = 'OK' | 'MISSING' | 'NOT_SET' | 'UNKNOWN'

export interface AppSettings {
  mediaBrowserPath:            string
  seriesThumbnailsPath:        string
  defaultInterludePath:        string
  emergencyLoopPath:           string
  liveLogoShowDelaySec:        number
  liveLogoHideBeforeEndSec:    number
  programLogoHideBeforeEndSec: number
  /** Présence sur disque des deux boucles, calculée par le serveur. */
  interludeHealth?: {
    defaultInterlude: InterludeFileState
    emergencyLoop:    InterludeFileState
  }
  /**
   * État réel du dossier de miniatures de séries, en lecture seule.
   *
   * `seriesThumbnailsPath` peut être neutralisé par l'environnement, et il n'est
   * de toute façon lu qu'au démarrage : `effectivePath` dit où le serveur écrit
   * réellement, `envVar` nomme la variable qui a imposé ce dossier s'il y en a
   * une.
   */
  seriesThumbnails: {
    effectivePath: string
    envVar:        string | null
    envPath:       string | null
  }
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const appSettingsApi = {
  get:    ()                        => get<AppSettings>('/settings'),
  update: (d: Partial<AppSettings>) => patch<AppSettings>('/settings', d),
}

export const mediaBrowserApi = {
  list:  (subPath?: string, q?: string, includeImages?: boolean, audioOnly?: boolean)  => {
    const params = new URLSearchParams()
    if (subPath) params.append('subPath', subPath)
    if (q) params.append('q', q)
    if (includeImages) params.append('images', '1')
    if (audioOnly) params.append('audio', '1')
    const qs = params.toString()
    return get<{ path: string; subPath: string; isSearch: boolean; files: MediaFile[]; count: number }>(`/media-browser${qs ? '?' + qs : ''}`)
  },
  probe: (filePath: string)  => post<ProbeResult>('/media-browser/probe', { filePath }),
}

export const clearGridApi = {
  clearRange: (channelId: string, from: string, to: string, gridId?: string | null) =>
    post<{ deleted: number; from: string; to: string }>('/items/clear-range', { channelId, from, to, ...(gridId ? { gridId } : {}) }),
}
